import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface MoodboardItem {
  id: string;
  brand_id: string;
  user_id: string;
  url: string;
  type: string;
  title?: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

export const useBrandMoodboard = (brandId: string) => {
  return useQuery({
    queryKey: ['brand-moodboard', brandId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('brand_moodboard')
        .select('*')
        .eq('brand_id', brandId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as MoodboardItem[];
    },
    enabled: !!brandId,
  });
};

export const useAddMoodboardItem = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (item: Omit<MoodboardItem, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('brand_moodboard')
        .insert([item])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['brand-moodboard', data.brand_id] });
      toast({
        title: "Inspiration added",
        description: "Your moodboard item has been added successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to add moodboard item.",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateMoodboardItem = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<MoodboardItem> & { id: string }) => {
      const { data, error } = await supabase
        .from('brand_moodboard')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['brand-moodboard', data.brand_id] });
      toast({
        title: "Inspiration updated",
        description: "Your moodboard item has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update moodboard item.",
        variant: "destructive",
      });
    },
  });
};

export const useDeleteMoodboardItem = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, brandId }: { id: string; brandId: string }) => {
      const { error } = await supabase
        .from('brand_moodboard')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { id, brandId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['brand-moodboard', data.brandId] });
      toast({
        title: "Inspiration removed",
        description: "Your moodboard item has been removed successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to remove moodboard item.",
        variant: "destructive",
      });
    },
  });
};